import * as types from "./ActionTypes";
import { LoginInfo, CustumizationUserInfo } from "@/types/LoginInfo";
import { LanguageState } from "@/hooks/useLanguage";
import { DashboardNotification, NotificationMessage } from "@/types/RootState";
import { LayersState } from "@/reducers/layers";

// History type for router navigation (compatible with react-router history object)
// eslint-disable-next-line @typescript-eslint/no-explicit-any
type RouterHistory = any;

// Base action interface for Redux 5 compatibility
interface BaseAction {
  [key: string]: unknown;
}

// Action type interfaces
export interface LoginAction extends BaseAction {
  type: typeof types.LOG_IN;
  username: string;
  password: string;
  history: RouterHistory;
}

export interface LogoutAction extends BaseAction {
  type: typeof types.LOG_OUT;
  callBack?: () => void;
}

export interface LoginSuccessAction extends BaseAction {
  type: typeof types.LOG_IN_SUCCESS;
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  data: LoginInfo | Record<string, any>;
}

export interface UpdateProfileAction extends BaseAction {
  type: typeof types.UPDATE_PROFILE;
  data: Partial<LoginInfo>;
}

export interface LanguageAction extends BaseAction {
  type: typeof types.LANGUAGE;
  data: LanguageState;
}

export interface SetHistoryCountAction extends BaseAction {
  type: typeof types.SET_HISTORY_COUNT;
  data: number;
}

export interface SetIsMobileAction extends BaseAction {
  type: typeof types.SET_IS_MOBILE;
  data: boolean | { isMobile: boolean };
}

export interface InterfaceAction extends BaseAction {
  type: typeof types.INTERFACE;
  data: CustumizationUserInfo | undefined;
}

export interface MessageAction extends BaseAction {
  type: typeof types.MESSAGE;
  data: { type: string; message: string };
}

export interface ShowCanvasMenuAction extends BaseAction {
  type: typeof types.SHOW_CANVAS_MENU;
  data: boolean | ((prev: boolean) => boolean);
}

export interface LogoPathAction extends BaseAction {
  type: typeof types.LOGO_PATH;
  data: string;
}

export interface MenuTypeAction extends BaseAction {
  type: typeof types.MENU_TYPE;
  data: "MAIN" | "ADMIN";
}

export interface StoreLayersAction extends BaseAction {
  type: typeof types.STORE_LAYERS;
  data: Partial<LayersState>;
}

export interface ReloadSidebarAction extends BaseAction {
  type: typeof types.RELOAD_SIDEBAR;
}

export interface DashboardNotificationAction extends BaseAction {
  type: typeof types.DASHBOARD_NOTIFICATION;
  data: DashboardNotification | null;
}

export interface GlobalNotificationAction extends BaseAction {
  type: typeof types.GLOBAL_NOTIFICATION;
  data: NotificationMessage[];
}

export interface GlobalNotificationCountAction extends BaseAction {
  type: typeof types.GLOBAL_NOTIFICATION_COUNT;
  data: number;
}

export interface ShowNotificationAction extends BaseAction {
  type: typeof types.SHOW_NOTIFICATION;
  data: boolean;
}

export interface ShowHelpSidebarAction extends BaseAction {
  type: typeof types.SHOW_HELP_SIDEBAR;
  data: boolean;
}

export interface ShowFavoritesSidebarAction extends BaseAction {
  type: typeof types.SHOW_FAVORITES_SIDEBAR;
  data: boolean;
}

export interface PreventSidebarAction extends BaseAction {
  type: typeof types.PREVENT_SIDEBAR;
  data: boolean;
}

export interface HidePlatformHeaderAction extends BaseAction {
  type: typeof types.HIDE_PLATFORM_HEADER;
  data: boolean;
}

export interface ShowAiChatAction extends BaseAction {
  type: typeof types.SHOW_AI_CHAT;
  data: boolean;
}

// Union type of all actions
export type AppAction =
  | LoginAction
  | LogoutAction
  | LoginSuccessAction
  | UpdateProfileAction
  | LanguageAction
  | SetHistoryCountAction
  | SetIsMobileAction
  | InterfaceAction
  | MessageAction
  | ShowCanvasMenuAction
  | LogoPathAction
  | MenuTypeAction
  | StoreLayersAction
  | ReloadSidebarAction
  | DashboardNotificationAction
  | GlobalNotificationAction
  | GlobalNotificationCountAction
  | ShowNotificationAction
  | ShowHelpSidebarAction
  | ShowFavoritesSidebarAction
  | PreventSidebarAction
  | HidePlatformHeaderAction
  | ShowAiChatAction;

// Action creators
export const login = (username: string, password: string, history: RouterHistory): LoginAction => {
  return {
    type: types.LOG_IN,
    username,
    password,
    history,
  };
};

export const logout = (callBack?: () => void): LogoutAction => {
  return {
    type: types.LOG_OUT,
    callBack,
  };
};

// eslint-disable-next-line @typescript-eslint/no-explicit-any
export const loginSuccess = (data: LoginInfo | Record<string, any>): LoginSuccessAction => {
  return {
    type: types.LOG_IN_SUCCESS,
    data,
  };
};

export const updateProfile = (data: Partial<LoginInfo>): UpdateProfileAction => {
  return {
    type: types.UPDATE_PROFILE,
    data,
  };
};

export const updateLanguage = (data: LanguageState): LanguageAction => {
  return {
    type: types.LANGUAGE,
    data,
  };
};

export const setHistoryCount = (count: number): SetHistoryCountAction => {
  return {
    type: types.SET_HISTORY_COUNT,
    data: count,
  };
};

export const setIsMobile = (data: boolean | { isMobile: boolean }): SetIsMobileAction => {
  return {
    type: types.SET_IS_MOBILE,
    data,
  };
};

export const updateCustumizationUserInfo = (
  data: CustumizationUserInfo | undefined
): InterfaceAction => {
  return {
    type: types.INTERFACE,
    data,
  };
};

export const updateMessage = (data: { type: string; message: string }): MessageAction => {
  return {
    type: types.MESSAGE,
    data,
  };
};

export const setShowCanvasMenu = (
  data: boolean | ((prev: boolean) => boolean)
): ShowCanvasMenuAction => {
  return {
    type: types.SHOW_CANVAS_MENU,
    data,
  };
};

export const setLogoPath = (data: string): LogoPathAction => {
  return {
    type: types.LOGO_PATH,
    data,
  };
};

export const setMenuType = (data: string): MenuTypeAction => {
  return {
    type: types.MENU_TYPE,
    data: data as "MAIN" | "ADMIN",
  };
};

export const storeLayer = (data: Partial<LayersState>): StoreLayersAction => {
  return {
    type: types.STORE_LAYERS,
    data,
  };
};

export const reloadSidebar = (): ReloadSidebarAction => {
  return {
    type: types.RELOAD_SIDEBAR,
  };
};

export const setDashboardNotification = (
  data?: DashboardNotification | null
): DashboardNotificationAction => {
  return {
    type: types.DASHBOARD_NOTIFICATION,
    data: data || null,
  };
};

export const setGlobalNotification = (
  notifications?: NotificationMessage[]
): GlobalNotificationAction => {
  return {
    type: types.GLOBAL_NOTIFICATION,
    data: notifications || [],
  };
};

export const setGlobalNotificationCount = (count: number): GlobalNotificationCountAction => {
  return {
    type: types.GLOBAL_NOTIFICATION_COUNT,
    data: count,
  };
};

export const setShowGlobalNotification = (data: boolean): ShowNotificationAction => {
  return {
    type: types.SHOW_NOTIFICATION,
    data: data,
  };
};

export const setShowHelpSidebar = (data: boolean): ShowHelpSidebarAction => {
  return {
    type: types.SHOW_HELP_SIDEBAR,
    data: data,
  };
};

export const setShowFavoritesSidebar = (data: boolean): ShowFavoritesSidebarAction => {
  return {
    type: types.SHOW_FAVORITES_SIDEBAR,
    data: data,
  };
};

export const preventSidebar = (data: boolean): PreventSidebarAction => {
  return {
    type: types.PREVENT_SIDEBAR,
    data: data,
  };
};

export const hidePlatformHeader = (data: boolean): HidePlatformHeaderAction => {
  return {
    type: types.HIDE_PLATFORM_HEADER,
    data: data,
  };
};

export const setShowAiChat = (data: boolean): ShowAiChatAction => {
  return {
    type: types.SHOW_AI_CHAT,
    data: data,
  };
};
